import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useSystemFeatures } from '../hooks/useSystemFeatures';

export default function TableEdit({ table, onSave, onCancel, theme, minX = 0, minY = 0, maxX = 1000, maxY = 1000 }) {
    const { t } = useLanguage();
    const { tableAreasEnabled } = useSystemFeatures();
    const minWidth = 1, minHeight = 1;
    const maxWidth = 250, maxHeight = 250;
    const [areas, setAreas] = useState([]);

    const [form, setForm] = useState({
        name: table.name || '',
        temporary: table.temporary || 0,
        shape: table.shape || '',
        width: table.width || 100,
        height: table.height || 100,
        x: table.x || 0,
        y: table.y || 0,
        area_id: table.area_id || 1,
    });

    // Areas are only offered where the tenant has the feature on; the column
    // itself exists everywhere and defaults to the seeded area 1.
    useEffect(() => {
        if (!tableAreasEnabled) return;
        fetch(`${BACKEND_URL}/api/table-areas`)
            .then(res => (res.ok ? res.json() : []))
            .then(data => setAreas(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching table areas:', err));
    }, [tableAreasEnabled]);

    function handleChange(e) {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        // Validation
        const { width, height, x, y } = form;
        if (
            width < minWidth || width > maxWidth ||
            height < minHeight || height > maxHeight ||
            x < minX || x > maxX ||
            y < minY || y > maxY
        ) {
            alert(`${t('widthHeightMustBe')} ${minWidth}-${maxWidth}, X ${t('mustBe')} ${minX}-${maxX}, Y ${t('mustBe')} ${minY}-${maxY}`);
            return;
        }
        fetch(`${BACKEND_URL}/api/tables/${table.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...table, ...form })
        })
        .then(async res => {
            if (res.ok) {
                onSave({ ...table, ...form }); // Parent will handle refresh
            } else {
                let errorMsg = t('error') + ': Failed to update table';
                try {
                    const data = await res.json();
                    if (data && data.error) errorMsg = data.error;
                } catch {}
                alert(errorMsg);
            }
        })
        .catch(err => {
            alert(t('networkError') + ': ' + (err?.message || 'Failed to update table'));
        });
    }

    return (
        <div style={{
            padding: '1rem',
            background: theme?.colors?.surface || '#fff',
            color: theme?.colors?.text || '#222',
            borderRadius: theme?.borderRadius || 8,
            boxShadow: theme?.boxShadow || '0 2px 8px #0002',
            maxWidth: 320,
            border: `2px solid ${theme?.colors?.primary || '#1976d2'}`
        }}>
            <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0 }}>{t('editTable')}</h3>
            <form onSubmit={handleSubmit}>
                <label>
                    {t('name')}:<br />
                    <input name="name" value={form.name} onChange={handleChange} />
                </label><br />
                <label>
                    {t('shape')}:<br />
                    <select name="shape" value={form.shape} onChange={handleChange}>
                        <option value="rectangle">{t('rectangle')}</option>
                        <option value="round">{t('round')}</option>
                    </select>
                </label><br />
                {tableAreasEnabled && areas.length > 0 && (
                    <>
                        <label>
                            {t('area')}:<br />
                            <select name="area_id" value={form.area_id} onChange={handleChange}>
                                {areas.map(area => (
                                    <option key={area.id} value={area.id}>{area.name}</option>
                                ))}
                            </select>
                        </label><br />
                    </>
                )}
                <label>
                    {t('width')} ({minWidth}-{maxWidth}):<br />
                    <input name="width" type="number" value={form.width} onChange={handleChange} onWheel={(e) => e.target.blur()} min={minWidth} max={maxWidth} />
                </label><br />
                <label>
                    {t('height')} ({minHeight}-{maxHeight}):<br />
                    <input name="height" type="number" value={form.height} onChange={handleChange} onWheel={(e) => e.target.blur()} min={minHeight} max={maxHeight} />
                </label><br />
                <label>
                    X ({minX}-{maxX}):<br />
                    <input name="x" type="number" value={form.x} onChange={handleChange} onWheel={(e) => e.target.blur()} min={minX} max={maxX} />
                </label><br />
                <label>
                    Y ({minY}-{maxY}):<br />
                    <input name="y" type="number" value={form.y} onChange={handleChange} onWheel={(e) => e.target.blur()} min={minY} max={maxY} />
                </label><br />
                <button type="submit" style={{ background: theme?.colors?.primary || '#1976d2', color: theme?.colors?.surface || '#fff', border: 'none', borderRadius: theme?.borderRadius || 8, padding: '0.5rem 1rem', cursor: 'pointer' }}>{t('save')}</button>
                <button type="button" onClick={onCancel} style={{ marginLeft: 8, background: theme?.colors?.secondary || '#aaa', color: theme?.colors?.surface || '#fff', border: 'none', borderRadius: theme?.borderRadius || 8, padding: '0.5rem 1rem', cursor: 'pointer' }}>{t('cancel')}</button>
            </form>
        </div>
    );
}
